import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { findBestResultsRangeToFetch, isInRange } from '~/components/AssetModalV2/Content/Panel/Transcript/utils';
import { useTranscriptSearchResultsContext } from '~/providers/TranscriptSearchResultsProvider';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { MAX_TRANSCRIPT_RANGE_IN_MS } from '~/swr-hooks/clips/transcript/constants';
import { type SearchTranscriptCache } from '~/swr-hooks/clips/transcript/search/types';
import { getSearchPrivateTranscriptKey } from '~/swr-hooks/clips/transcript/search/useSearchPrivateTranscript';
import { type TimeRange } from '~/swr-hooks/clips/transcript/types';
import { useAirStore } from '~/utils/ReduxUtils';

/**
 * This hook is responsible for fetching search results for segments that are visible for a while on the screen.
 */
export const useFetchSearchResultsForSegmentsInView = () => {
  const { getSearchText, fetchSearchResultsForRange } = useTranscriptSearchResultsContext();
  const store = useAirStore();
  const queryClient = useQueryClient();

  const fetchingRanges = useRef<TimeRange[]>([]);

  const shouldFetchResultsForRange = useCallback((timeRange: TimeRange, searchCache: SearchTranscriptCache) => {
    let previousEndTime: number | null = null;
    let nextStartTime: number | null = null;

    for (let i = 0; i < searchCache.data.length; i++) {
      const range = searchCache.data[i];

      const isRangeAlreadyFetched =
        isInRange(timeRange.startTimeMs, [range.startTimeMs, range.endTimeMs]) &&
        isInRange(timeRange.endTimeMs, [range.startTimeMs, range.endTimeMs]);

      if (isRangeAlreadyFetched) {
        return {
          shouldFetch: false,
          previousEndTime,
          nextStartTime,
        };
      }

      const isRangeAlreadyFetching = fetchingRanges.current.some(
        (range) =>
          isInRange(timeRange.startTimeMs, [range.startTimeMs, range.endTimeMs]) &&
          isInRange(timeRange.endTimeMs, [range.startTimeMs, range.endTimeMs]),
      );

      if (isRangeAlreadyFetching) {
        return {
          shouldFetch: false,
          previousEndTime,
          nextStartTime,
        };
      }

      if (range.endTimeMs <= timeRange.startTimeMs) {
        previousEndTime = range.endTimeMs;
      } else if (range.startTimeMs >= timeRange.endTimeMs) {
        nextStartTime = range.startTimeMs;
        // if range is later than timeRange, we don't have to continue checking
        break;
      }
    }

    return {
      previousEndTime,
      nextStartTime,
      shouldFetch: true,
    };
  }, []);

  const onSegmentPersistedInView = useCallback(
    (timeRange: TimeRange) => {
      const searchText = getSearchText();
      if (!searchText) return;

      const clipId = centralizedClipIdSelector(store.getState());
      if (!clipId) return;

      const searchTranscriptCacheKey = getSearchPrivateTranscriptKey(clipId, searchText);
      const searchTranscriptCache = queryClient.getQueryData<SearchTranscriptCache>(searchTranscriptCacheKey);

      if (!searchTranscriptCache?.hasMore) return;

      const { shouldFetch, nextStartTime, previousEndTime } = shouldFetchResultsForRange(
        timeRange,
        searchTranscriptCache,
      );
      if (!shouldFetch) return;

      const timeRangeToFetch = findBestResultsRangeToFetch({
        timeRange,
        previousEndTime,
        nextStartTime,
        maxRangeSize: MAX_TRANSCRIPT_RANGE_IN_MS,
      });

      fetchingRanges.current.push(timeRangeToFetch);

      fetchSearchResultsForRange(timeRangeToFetch).finally(() => {
        fetchingRanges.current.splice(fetchingRanges.current.indexOf(timeRangeToFetch), 1);
      });
    },
    [fetchSearchResultsForRange, getSearchText, queryClient, shouldFetchResultsForRange, store],
  );

  return {
    onSegmentPersistedInView,
  };
};
